import { LoadingStageModel, ValueModel } from '@kts-front/models';
import { Nullable } from '@kts-front/types';
import { action, computed, IReactionDisposer, makeObservable, reaction } from 'mobx';

import { ENDPOINTS } from 'configs/endpoints';
import { ClientEntity, ClientEntityApi, clientListSchema, clientSchema } from 'entities/Client';
import { LeadEntity, LeadEntityApi, leadSchema } from 'entities/Lead';
import { MeetingCreateEntityApi } from 'entities/Meeting/MeetingCreate';
import { logApiErrWithSentry, logWithSentry, SentryErrorKind } from 'scripts/initSentry';
import { apiStore } from 'stores/global/ApiStore';
import { FieldSelectClientLeadStore } from 'stores/local/shared/FieldSelectClientLeadStore';
import { CreateClientStore } from 'stores/local/shared/FixationClientDrawerStore/CreateClientStore';
import { PopupStore } from 'stores/local/shared/PopupStore';
import { ProjectTimeSlotStore } from 'stores/local/shared/ProjectTimeSlotStore/ProjectTimeSlotStore';
import { CreateSubAgentStore, getSubAgentOptions } from 'stores/local/shared/SubAgentStore';
import { FieldSelectStore } from 'stores/local/shared/form/FieldSelectStore';
import { CreateMeetingDrawerModalMode } from 'types/CreateMeetingDrawerModalMode';
import { OptionEntity } from 'types/OptionEntity';
import { createFullName } from 'utils/createFullName';
import { isDefined } from 'utils/isDefined';
import { validateNotEmpty } from 'utils/validators';

import { ClientUniquenessStore } from '../ClientUniquenessStore';
import { rootStore } from '../RootStore';
import { CheckUniqueClientMethod, ClientEditFieldsKey, MeetingFields } from '../RootStore/AppConfigStore';
import { NotificationsStore } from '../RootStore/NotificationsStore';

type ClientOptionEntity = OptionEntity & {
  phone?: string;
};

type Params = {
  notificationStore: NotificationsStore;
  optionalFields: Set<MeetingFields>;
  shownFields: Set<MeetingFields>;
  checkUniqueClientMethod: CheckUniqueClientMethod;
  filterByUniqueClientsAndLeads: boolean;
};

export class CreateMeetingDrawerModal extends PopupStore<number> {
  private readonly _notificationStore: NotificationsStore;

  private readonly _loadClientsRequest = apiStore.createRequest({
    url: ENDPOINTS.client.leadList,
  });

  private readonly _loadLeadRequest = (id: number) =>
    apiStore.createRequest({
      url: ENDPOINTS.lead.get(id),
    });

  private readonly _loadClientRequest = (id: number) =>
    apiStore.createRequest({
      url: ENDPOINTS.client.get(id),
    });

  readonly loadingStage = new LoadingStageModel();

  readonly leadValue = new ValueModel<Nullable<LeadEntity>>(null);

  readonly leadLoadingStage = new LoadingStageModel();

  readonly clientValue = new ValueModel<Nullable<ClientEntity>>(null);

  readonly clientLoadingStage = new LoadingStageModel();

  readonly createLoadingStage = new LoadingStageModel();

  readonly clientUniquenessStore = new ClientUniquenessStore({
    checkUniquePhone: rootStore.appConfigStore.logic.checkUniquePhone,
  });

  readonly isStaticMode = new ValueModel<Nullable<CreateMeetingDrawerModalMode>>(null);

  private readonly _createMeetingRequest = apiStore.createRequest({
    url: ENDPOINTS.meeting.create,
    method: 'POST',
  });

  protected readonly _client: FieldSelectStore<ClientOptionEntity>;

  protected readonly _subAgent: FieldSelectStore;

  projectTimeSlot: ProjectTimeSlotStore;

  private _leadSelectStore: FieldSelectClientLeadStore;

  private readonly _disposers: IReactionDisposer[];

  private readonly _filterByUniqueClientsAndLeads: boolean;

  readonly createClientStore = new CreateClientStore({
    onCreated: () => {
      // Сохраняем projectId ДО resetFields, если проверка была по телефону и проекту
      const isPhoneAndProjectCheck =
        this.clientUniquenessStore.checkUniqueClient.value === CheckUniqueClientMethod.phoneAndProject;
      const projectId = this.clientUniquenessStore.projectId.value;
      const savedProjectId = isPhoneAndProjectCheck && projectId ? projectId : null;

      this.isStaticMode.change(CreateMeetingDrawerModalMode.openNewMeeting);
      this._client.load(true).then(() => {
        // Выбираем первого клиента из списка после загрузки
        if (this._client.options.length > 0) {
          this._client.setValueByKey(this._client.options[0].key);
        }
      });
      this.resetFields();

      // Если проверка была по телефону и проекту, подставляем сохраненный projectId
      if (savedProjectId) {
        this.projectTimeSlot.projects.projectId.change(savedProjectId);
        this.projectTimeSlot.projects.load();
      }
    },
    onNextStep: () => {},
    checkUniqueClientStore: this.clientUniquenessStore,
  });

  readonly createSubAgentStore = new CreateSubAgentStore({
    onCreated: () => {
      this.isStaticMode.change(CreateMeetingDrawerModalMode.openNewMeeting);
      this._subAgent.load(true).then(() => {
        if (this._subAgent.options.length > 0) {
          this._subAgent.setValueByKey(this._subAgent.options[0].key);
        }
      });
    },
    onNextStep: () => {},
  });

  constructor({
    notificationStore,
    optionalFields,
    shownFields,
    checkUniqueClientMethod,
    filterByUniqueClientsAndLeads,
  }: Params) {
    super();

    this._notificationStore = notificationStore;
    this._filterByUniqueClientsAndLeads = filterByUniqueClientsAndLeads;

    this.clientUniquenessStore.checkUniqueClient.change(checkUniqueClientMethod);

    this._client = new FieldSelectStore<ClientOptionEntity>({
      getOptions: this.getClientOptions,
      required: true,
      validator: validateNotEmpty('Выберите клиента'),
      clientUniquenessStore: this.clientUniquenessStore,
    });

    this.projectTimeSlot = new ProjectTimeSlotStore({
      isProjectSelectRequired: !optionalFields.has(MeetingFields.projectId),
      isProjectSelectShown: shownFields.has(MeetingFields.projectId),
      isSlotSelectRequired: !optionalFields.has(MeetingFields.slotId),
      isSlotSelectShown: shownFields.has(MeetingFields.slotId),
      getLeadId: () => {
        const key = this._leadSelectStore.value[0]?.key;

        return key ? Number(key) : null;
      },
    });

    this._leadSelectStore = new FieldSelectClientLeadStore({
      shown: shownFields.has(MeetingFields.leadId),
      required: !optionalFields.has(MeetingFields.leadId),
      validator: !optionalFields.has(MeetingFields.leadId) ? validateNotEmpty('Выберите заявку') : undefined,
      filterByUniqueLeads: filterByUniqueClientsAndLeads,
    });

    this._subAgent = new FieldSelectStore({
      getOptions: getSubAgentOptions,
      required: !optionalFields.has(MeetingFields.subAgent),
      shown: shownFields.has(MeetingFields.subAgent),
    });

    makeObservable(this, {
      client: computed,
      subAgent: computed,
      leadSelectStore: computed,
      isError: computed,
      isTouched: computed,
      isProjectSelected: computed,
      isClientSelected: computed,
      hasLeadOptions: computed,

      resetFields: action.bound,
      validate: action.bound,
      getClientOptions: action.bound,
      createMeeting: action.bound,
      leadFetch: action.bound,
      openWithClient: action.bound,
      openWithLead: action.bound,
      openNewMeeting: action.bound,
      clientFetch: action.bound,
    });

    this._disposers = [
      reaction(
        () => this._client.value,
        (client) => {
          const clientId = client[0]?.key;
          const phone = client[0]?.phone;

          this._leadSelectStore.resetOptions();
          this._leadSelectStore.resetValue();
          if (this.clientUniquenessStore.shouldCheckUniqueByPhoneOnly) {
            this.projectTimeSlot.resetFields();
          }
          this._leadSelectStore.projectID.change(null);

          if (isDefined(clientId)) {
            this._leadSelectStore.clientId.change(Number(clientId));
            this._leadSelectStore.load(true);
          }

          // Проверяем уникальность при изменении клиента, если включена проверка по телефону и проекту
          if (
            this.clientUniquenessStore.shouldCheckUniqueByPhoneAndProject &&
            phone &&
            this.clientUniquenessStore.projectId.value
          ) {
            this.clientUniquenessStore.checkUnique(phone);
          }
        },
      ),

      reaction(
        () => this._leadSelectStore.projectID.value,
        (id) => {
          if (this.clientUniquenessStore.shouldCheckUniqueByPhoneAndProject) {
            return;
          }
          this.projectTimeSlot.projects.projectId.change(id ?? null);
          this.projectTimeSlot.projects.load();
        },
      ),

      reaction(
        () => this.projectTimeSlot.projects.value[0]?.key,
        (projectKey) => {
          this.clientUniquenessStore.projectId.change(projectKey ? Number(projectKey) : null);
        },
      ),

      reaction(
        () => this.clientUniquenessStore.projectId.value,
        () => {
          // Если проверка идет по телефону и проекту, перепроверяем уникальность при изменении проекта
          if (
            this.clientUniquenessStore.shouldCheckUniqueByPhoneAndProject &&
            isDefined(this.clientUniquenessStore.projectId.value)
          ) {
            // Используем телефон выбранного клиента или из формы создания
            const selectedClient = this._client.value[0];
            const phone = selectedClient?.phone || this.createClientStore.fieldsMap[ClientEditFieldsKey.phone].value;

            if (phone) {
              this.clientUniquenessStore.checkUnique(phone);
            }
          }
        },
      ),

      reaction(
        () => this._leadSelectStore.value[0]?.key,
        () => {
          if (this.projectTimeSlot.slot.projectId.value && this.projectTimeSlot.date.value) {
            this.projectTimeSlot.slot.load(true);
          }
        },
      ),
    ];
  }

  get client() {
    return this._client;
  }

  get subAgent() {
    return this._subAgent;
  }

  get leadSelectStore() {
    return this._leadSelectStore;
  }

  get hasLeadOptions(): boolean {
    return this._leadSelectStore.options.length !== 0;
  }

  get isClientSelected(): boolean {
    return (
      Boolean(this._client.value.length !== 0 && this._client.value[0].key) || isDefined(this.clientValue.value?.id)
    );
  }

  get isProjectSelected(): boolean {
    const hasProject = this.projectTimeSlot.projects.value.length > 0;

    return hasProject || !this.hasLeadOptions;
  }

  get isError(): boolean {
    return this.client.hasError || this.projectTimeSlot.isError || this._leadSelectStore.hasError;
  }

  get isTouched(): boolean {
    const isLeadSelected = !this._leadSelectStore.required || this._leadSelectStore.value.length > 0;
    const { slot } = this.projectTimeSlot;
    const isSlotFilled = !slot.shown || !slot.required || slot.value.length > 0;

    return this.isClientSelected && this.projectTimeSlot.isTouched && isLeadSelected && isSlotFilled;
  }

  getClientOptions = async (params: { limit: number; offset: number; search: string; keys: string[] }) => {
    try {
      const requestPayload: Record<string, unknown> = {
        limit: params.limit,
        offset: params.offset,
        search: params.search || undefined,
      };

      if (this._filterByUniqueClientsAndLeads) {
        requestPayload.status__in = 'unique';
      }

      const response = await this._loadClientsRequest.call<{
        items: ClientEntityApi[];
        total: number;
      }>({
        params: requestPayload,
      });

      if (response.isError) {
        logApiErrWithSentry({
          endpoint: ENDPOINTS.client.leadList,
          method: 'GET',
          requestPayload,
          response,
        });

        return {
          isError: true,
          data: { items: [], total: 0 },
        };
      }

      const parsed = clientListSchema.safeParse(response.data);

      return {
        isError: false,
        data: {
          items:
            parsed.data?.items?.map((item) => ({
              key: item.id.toString(),
              value: createFullName(item),
              phone: item.phone,
            })) || [],
          total: response.data.total,
        },
      };
    } catch (e) {
      logWithSentry(e, SentryErrorKind.normalization);

      return {
        isError: true,
        data: { items: [], total: 0 },
      };
    }
  };

  async createMeeting() {
    if (this.isError) {
      return;
    }

    this.createLoadingStage.loading();

    const requestPayload = {
      client_id: this._client.value.length !== 0 ? Number(this._client.value[0].key) : this.clientValue.value?.id,
      lead_id:
        this.leadSelectStore.value.length !== 0
          ? Number(this.leadSelectStore.value[0].key)
          : (this.leadValue.value?.id ?? null),
      project_id: this.projectTimeSlot.projects.value[0]?.key
        ? Number(this.projectTimeSlot.projects.value[0].key)
        : null,
      is_offline: this.projectTimeSlot.isOffline.value[0]?.key === 'true',
      slot_id: this.projectTimeSlot.slot.value[0]?.key ? Number(this.projectTimeSlot.slot.value[0].key) : null,
      sub_agent_id: this._subAgent.value[0]?.key ? Number(this._subAgent.value[0].key) : null,
    };

    const response = await this._createMeetingRequest.call<MeetingCreateEntityApi>({
      data: requestPayload,
    });

    if (response.isError) {
      this.createLoadingStage.error();
      this._notificationStore.showErrorByResponse(response);

      logApiErrWithSentry({
        endpoint: ENDPOINTS.meeting.create,
        method: 'POST',
        requestPayload,
        response,
      });

      return { Error: true };
    }

    this.createLoadingStage.success();
    this._notificationStore.showSuccess('Встреча создана');
    this.close();
  }

  async leadFetch(id: number): Promise<void> {
    this.leadLoadingStage.loading();
    const response = await this._loadLeadRequest(id).call<LeadEntityApi>();

    if (response.isError) {
      this.loadingStage.error();
      this.leadLoadingStage.error();

      this._notificationStore.showErrorByResponse(response);

      logApiErrWithSentry({
        endpoint: ENDPOINTS.lead.get(id),
        method: 'GET',
        requestPayload: {},
        response,
      });

      return;
    }

    const parsed = leadSchema.safeParse(response.data);

    if (parsed.error || !parsed.data) {
      this.leadLoadingStage.error();
      this.loadingStage.error();
      this._notificationStore.showErrorByResponse(response);

      return;
    }

    this.resetFields();
    this.loadingStage.success();
    this.leadValue.change(parsed.data);
    this.clientValue.change(parsed.data.client);
    this.projectTimeSlot.projects.projectId.change(parsed.data.project?.id ?? null);
    await this.projectTimeSlot.projects.load();

    this._leadSelectStore.clientId.change(parsed.data.client.id);
    await this._leadSelectStore.load(true);
    this.leadSelectStore.setValueByKey(parsed.data.id.toString());

    this.leadLoadingStage.success();
  }

  async clientFetch(id: number): Promise<void> {
    this.clientLoadingStage.loading();
    const response = await this._loadClientRequest(id).call<ClientEntityApi>();

    if (response.isError) {
      logApiErrWithSentry({
        endpoint: ENDPOINTS.client.get(id),
        method: 'GET',
        requestPayload: {},
        response,
      });
      this.loadingStage.error();
      this.clientLoadingStage.error();
      this._notificationStore.showErrorByResponse(response);

      return;
    }

    const parsed = clientSchema.safeParse(response.data);

    if (parsed.error || !parsed.data) {
      this.loadingStage.error();
      this.clientLoadingStage.error();
      this._notificationStore.showErrorByResponse(response);

      return;
    }

    this.resetFields();
    this.loadingStage.success();
    this.clientValue.change(parsed.data);
    this._client.setValueByKey(id.toString());
    this._client.load();

    this._leadSelectStore.resetOptions();
    this._leadSelectStore.resetValue();
    this._leadSelectStore.clientId.change(id);
    await this._leadSelectStore.load();

    this.projectTimeSlot.projects.projectId.change(null);
    await this.projectTimeSlot.projects.load();
    this.clientLoadingStage.success();
  }

  openWithClient(id: number): void {
    super.open(id);
    this.isStaticMode.change(CreateMeetingDrawerModalMode.openWithClient);
    this.clientFetch(id);
  }

  openWithLead(id: number): void {
    this.isStaticMode.change(CreateMeetingDrawerModalMode.openWithClientAndLead);
    super.open(id);
    this.leadFetch(id);
  }

  openNewMeeting(): void {
    this._client.load(true);
    this._subAgent.load(true);
    this.isStaticMode.change(CreateMeetingDrawerModalMode.openNewMeeting);
    this.resetFields();
    super.openWithNull();
    this.projectTimeSlot.projects.projectId.change(null);
    this.projectTimeSlot.projects.load();
  }

  validate(): boolean {
    this.client.validate();
    this.projectTimeSlot.validate();
    this._leadSelectStore.validate();

    return this.isError;
  }

  resetFields() {
    this.client.reset();
    this._leadSelectStore.reset();
    this._leadSelectStore.resetOptions();
    this.projectTimeSlot.resetFields();
    this.subAgent.reset();
  }

  close(): void {
    super.close();
    this.resetFields();
    this.createClientStore.reset();
    this.createSubAgentStore.reset();
  }

  destroy() {
    this._disposers.forEach((d) => d());
    this.clientUniquenessStore.destroy();
  }
}
